import { AVATAR_SIZES } from '#app/components/Avatars/Avatar/constants.js';
import {
  type AvatarSize,
  type ContraImage,
} from '#app/components/Avatars/Avatar/types.js';
import { ProfileImageCropperContext } from '#app/components/ProfileImageCropper/context/ProfileImageCropperContext.js';
import { HoverCropperButton } from '#app/components/ProfileImageCropper/HoverCropperButton.js';
import { ProfileImageCropperUploadInput } from '#app/components/ProfileImageCropper/ProfileImageCropperUploadInput.js';
import {
  type ProfileImageCropperContextData,
  type ProfileImageCropperContextTypes,
} from '#app/components/ProfileImageCropper/types.js';
import { TextStyles } from '#app/theme/textStyles.js';
import {
  AvatarHoverBox,
  StyledAvatar,
  StyledAvatarEditorBox,
  StyledEditableAvatarBox,
} from './EditableAvatar.styles.js';
import { CameraIcon } from '@contra/icons/icons';
import { type ReactNode, useContext, useRef } from 'react';

const LARGE_EDITABLE_AVATAR_DIMENSIONS = AVATAR_SIZES.huge;

type EditableAvatarProps = {
  readonly avatarImage?: ContraImage | null;
  readonly avatarSize?: AvatarSize | null;
  readonly context?: ProfileImageCropperContextTypes;
  readonly fallbackInitialsLength?: number;
  readonly fallbackInitialsTextStyle?: TextStyles;
  readonly fallbackName?: string;
  readonly shouldSaveProfileOnUpload?: boolean;
  readonly showBorder?: boolean;
  readonly variant?: 'badge' | 'hover';
};

const getEditableAvatarMetadataLabels = (
  context: Required<EditableAvatarProps['context']>,
) => {
  switch (context) {
    case 'AVATAR':
      return {
        avatarLabel: 'Profile picture',
        inputId: 'profileAvatarUpload',
      };
    case 'BRAND_CUSTOMIZATION_LOGO':
      return {
        avatarLabel: 'Brand logo',
        inputId: 'brandCustomizationLogo',
      };
    case 'CONTACT':
      return {
        avatarLabel: 'Contact picture',
        inputId: 'contactAvatarUpload',
      };
    case 'LOGO':
      return {
        avatarLabel: 'Company logo',
        inputId: 'companyLogoUpload',
      };
    default:
      throw new Error(`Unhandled EditableAvatar context: ${context}`);
  }
};

const AvatarBox = ({
  children,
  variant,
  ...props
}: {
  readonly children: ReactNode;
  readonly variant: EditableAvatarProps['variant'];
}) => {
  return variant === 'badge' ? (
    <StyledEditableAvatarBox {...props}>{children}</StyledEditableAvatarBox>
  ) : (
    <AvatarHoverBox {...props}>{children}</AvatarHoverBox>
  );
};

export const EditableAvatar = ({
  avatarImage,
  avatarSize,
  context = 'AVATAR',
  fallbackInitialsLength,
  fallbackInitialsTextStyle,
  fallbackName,
  shouldSaveProfileOnUpload = false,
  showBorder = true,
  variant = 'badge',
  ...props
}: EditableAvatarProps) => {
  const formInputElement = useRef<HTMLInputElement>(null);
  const { croppedPreview, handleUpload } =
    useContext<ProfileImageCropperContextData>(ProfileImageCropperContext);
  const { avatarLabel, inputId } = getEditableAvatarMetadataLabels(context);

  return (
    <>
      <ProfileImageCropperUploadInput
        id={inputId}
        ref={formInputElement}
      />
      <AvatarBox
        variant={variant}
        {...props}
      >
        <StyledAvatar
          $showBorder={showBorder}
          aria-label={avatarLabel}
          avatarImage={avatarImage}
          fallbackInitialsLength={fallbackInitialsLength}
          fallbackInitialsTextStyle={fallbackInitialsTextStyle}
          fallbackName={fallbackName}
          previewImage={croppedPreview[context]}
          size={avatarSize ?? LARGE_EDITABLE_AVATAR_DIMENSIONS}
        />

        {variant === 'badge' ? (
          <div>
            <StyledAvatarEditorBox
              as="button"
              onClick={() =>
                handleUpload(formInputElement, {
                  context,
                  shouldSaveProfileOnUpload,
                })
              }
            >
              <CameraIcon size={24} />
            </StyledAvatarEditorBox>
          </div>
        ) : null}

        {variant === 'hover' ? (
          <HoverCropperButton
            context={context}
            formInputElement={formInputElement}
            handleUpload={handleUpload}
            shouldSaveProfileOnUpload={shouldSaveProfileOnUpload}
            size={avatarSize ?? LARGE_EDITABLE_AVATAR_DIMENSIONS}
          />
        ) : null}
      </AvatarBox>
    </>
  );
};
